import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { flocknoteKeys } from "Flocknote/API/keys";
import { useGetUserData } from "Flocknote/API/queries";

import useIsUnloading from "Utilities/Hooks/useIsUnloading";
import { useLocalStorage } from "Utilities/Hooks/useLocalStorage";
import { formatPhoneNumber } from "Utilities/Number";

import { useTwoFAContext } from "../TwoFA";
import { LAST_EMAIL_PHONE } from "../constants";
import {
  useClearNetworkPayload,
  usePostData,
  useRequestAuthCodePayload,
  useStartOverPayload,
} from "../stateHooks";

import {
  useClearError,
  usePostTwoFactorAuth,
  usePostUpdateName,
} from "./queries";

const defaultObject = {};
/**
 * creates a tuple out of api state and refetch function
 * @returns {[TwoFAResponse, function]}
 */
const use2FAState = () => {
  const [hookRefetching, setHookRefetching] = useState(false);

  // If the browser is redirecting, leave the load states running
  const isUnloading = useIsUnloading();
  const { variant } = useTwoFAContext();

  const postData = usePostData();
  const { data, refetch: refetchRaw } = usePostTwoFactorAuth({
    postData,
    variant,
  });

  const refetch = useCallback(async () => {
    setHookRefetching(true);
    await refetchRaw();
    setHookRefetching(false);
  }, [refetchRaw]);

  return [
    { ...(data || defaultObject), isLoading: hookRefetching || isUnloading },
    refetch,
  ];
};

const useNetworkKnown = () => {
  const [{ networkName, networkPhotoUrl }] = use2FAState();

  const hasPhoto = Boolean(networkPhotoUrl);
  const hasNetworkName = Boolean(networkName);

  return Boolean(hasPhoto || hasNetworkName);
};

const useUpdateName = () => {
  const [{ isLoading: stateIsLoading }, refetch] = use2FAState();
  const { mutate, isLoading: isUpdating } = usePostUpdateName({
    onSuccess: refetch,
  });

  const {
    data: { userID },
    refetch: refetchUserData,
  } = useGetUserData();

  useEffect(() => {
    if (!userID) refetchUserData();
  }, [refetchUserData, userID]);

  return [{ isLoading: stateIsLoading || isUpdating }, mutate];
};

/**
 * If a phone number, it is formatted in the standard way. If an email, it is forwarded as such.
 * @returns {string} - The first factor to be used for displaying to the user
 */
const useFormattedFirstFactor = () => {
  const [{ firstFactor, firstFactorType }] = use2FAState();
  const [lastValue, setLastValue] = useState();

  const formattedFirstFactor = useMemo(() => {
    // once we have a value, don't let go of it!
    if (!firstFactor) return lastValue;
    if (firstFactorType === "phone") return formatPhoneNumber(firstFactor);
    return firstFactor;
  }, [firstFactor, firstFactorType, lastValue]);

  if (lastValue !== formattedFirstFactor) {
    setLastValue(formattedFirstFactor);
  }

  return formattedFirstFactor;
};

/**
 * Returns a tuple with the api data for convenience. The function will clear the user state from the backend so the user can start over.
 * @returns {[TwoFAResponse, function]} - The api response data and
 */
const useStartOver = ({ variant, remoteCancelRef }) => {
  const [hookRefetching, setHookRefetching] = useState(false);
  const startOverPayload = useStartOverPayload();

  const { data, refetch: refetchRaw } = usePostTwoFactorAuth({
    postData: startOverPayload,
    variant,
  });

  if (typeof remoteCancelRef === "object") remoteCancelRef.current = refetchRaw;

  const refetch = useCallback(async () => {
    setHookRefetching(true);
    await refetchRaw();
    setHookRefetching(false);
  }, [refetchRaw]);

  return [{ ...(data || defaultObject), isLoading: hookRefetching }, refetch];
};

/**
 *
 * @returns {[TwoFAResponse, function]} - The api response data and refetch function
 */
const useClearNetwork = () => {
  const [hookRefetching, setHookRefetching] = useState(false);
  const clearNetworkPayload = useClearNetworkPayload();
  const { variant } = useTwoFAContext();

  const { data, refetch: refetchRaw } = usePostTwoFactorAuth({
    postData: clearNetworkPayload,
    variant,
  });
  const refetch = useCallback(async () => {
    setHookRefetching(true);
    await refetchRaw();
    setHookRefetching(false);
  }, [refetchRaw]);
  return [{ ...(data || defaultObject), isLoading: hookRefetching }, refetch];
};

const useRequestAuthCode = () => {
  const requestAuthCodePaylod = useRequestAuthCodePayload();
  const [resending, setResending] = useState(false);
  const { variant } = useTwoFAContext();
  const { refetch } = usePostTwoFactorAuth({
    postData: requestAuthCodePaylod,
    variant,
  });

  const resendCode = async () => {
    setResending(true);
    await refetch();
    setResending(false);
  };

  return { resendCode, resending };
};

const useClearErrorOnChange = () => {
  const { variant, firstFactor, secondCode, firstCode, password, usePassword } =
    useTwoFAContext();
  const clearError = useClearError({ variant });

  useEffect(clearError, [
    clearError,
    firstFactor,
    secondCode,
    firstCode,
    password,
    usePassword,
  ]);
};

const useStoreFirstFactor = () => {
  const { firstFactor } = useTwoFAContext();
  const [, setLastFirstFactorStorage] = useLocalStorage(LAST_EMAIL_PHONE);

  const storeFirstFactor = useCallback(() => {
    setLastFirstFactorStorage({ value: firstFactor });
  }, [firstFactor, setLastFirstFactorStorage]);

  return storeFirstFactor;
};

const useOnSuccessPlus = () => {
  const [apiState] = use2FAState();
  const queryClient = useQueryClient();
  const { refetch: refetchUserData } = useGetUserData();
  const { onSuccess: onSuccessRaw } = useTwoFAContext();
  const storeFirstFactor = useStoreFirstFactor();

  const onSuccessPlus = useCallback(async () => {
    queryClient.removeQueries({ queryKey: flocknoteKeys.permissions() });
    storeFirstFactor();
    const {
      data: { userID },
    } = await refetchUserData();
    if (typeof onSuccessRaw === "function")
      onSuccessRaw({
        ...apiState,
        userID,
        // aliasing these for older code.
        flexibleInputVal: apiState.firstFactor,
        userInfo: apiState.givePageUserInfo,
      });
  }, [apiState, onSuccessRaw, queryClient, refetchUserData, storeFirstFactor]);

  if (!onSuccessRaw) return storeFirstFactor;

  return onSuccessPlus;
};

export {
  use2FAState,
  useFormattedFirstFactor,
  useStartOver,
  useClearNetwork,
  useRequestAuthCode,
  useUpdateName,
  useNetworkKnown,
  useClearErrorOnChange,
  useOnSuccessPlus,
  useStoreFirstFactor,
};
